import React, { useState } from 'react';
import { ArrowRightIcon, ChevronDownIcon, MapPinIcon } from 'lucide-react';
import { SmartLink } from '../SmartLink';
import { SubscribeForm } from '../whatsnew/SubscribeForm';
import { departments, jobTypes } from '../../data/careers';
import type { Job } from '../../types/career';
export function JobList({
  jobs
}: {
  jobs: Job[];
}) {
  const [department, setDepartment] = useState('');
  const [type, setType] = useState('');
  const filtered = jobs.filter(j => (!department || j.department === department) && (!type || j.type === type));
  const groups = departments.map(d => ({
    department: d,
    jobs: filtered.filter(j => j.department === d)
  })).filter(g => g.jobs.length > 0);
  return <div data-id="element-242" data-mp="3nb8gk07kne13">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:flex lg:justify-start" data-id="element-243" data-mp="3nap6t6s2rvy5">
        <Select id="role-department" label="Department" value={department} onChange={setDepartment} allLabel="All departments" options={departments} data-id="element-244" data-mp="1hi9z5qwzetvd" />
        <Select id="role-type" label="Type" value={type} onChange={setType} allLabel="All types" options={jobTypes} data-id="element-245" data-mp="253gr1won26fo" />
      </div>
      <p className="mt-4 text-[14px] text-ink-muted" role="status" data-id="element-246" data-mp="2055h7lycfvxg">
        {filtered.length} open {filtered.length === 1 ? 'role' : 'roles'}
      </p>

      {groups.length === 0 ? <div className="mt-8 rounded-[14px] border border-canvas-line px-6 py-10 text-center" data-id="element-247" data-mp="29ugkgryju6df">
          <p className="text-[18px] font-semibold text-ink" data-id="element-248" data-mp="1m4cj6r8u2ipl">No open roles right now</p>
          <p className="mx-auto mt-2 max-w-[420px] text-[15px] text-ink-muted" data-id="element-249" data-mp="1h5j6h11devqh">
            Leave your email and we’ll tell you when a role that matches opens up.
          </p>
          <SubscribeForm id="roles-notify" buttonLabel="Get notified" successText="Thanks — we’ll email you about new roles." className="mt-6" data-id="element-250" data-mp="1w0pc3mwk0sja" />
          {(department || type) && <button type="button" onClick={() => {
        setDepartment('');
        setType('');
      }} className="mt-5 text-[14px] font-semibold text-brand hover:text-brand-hover" data-id="element-251" data-mp="32jduy8wiqyvm">
              Clear filters
            </button>}
        </div> : <div className="mt-8 space-y-10" data-id="element-252" data-mp="3jl03axayyibw">
          {groups.map(group => <section key={group.department} aria-labelledby={`dept-${slugify(group.department)}`} data-id="element-253" data-mp="3oi0chh348178">
              <h3 id={`dept-${slugify(group.department)}`} className="flex items-baseline gap-2 text-[18px] font-semibold text-ink" data-id="element-254" data-mp="2qwvuw410jr5i">
                {group.department}
                <span className="text-[14px] font-medium text-ink-muted" data-id="element-255" data-mp="30t79x1rzmv3n">{group.jobs.length}</span>
              </h3>
              <ul className="mt-4 space-y-3 md:space-y-0 md:border-t md:border-canvas-line" data-id="element-256" data-mp="35lilx30067ck">
                {group.jobs.map(job => <li key={job.slug} data-id="element-257" data-mp="282z3262wjx4d">
                    <SmartLink href={`/careers/${job.slug}`} className="group grid grid-cols-1 gap-3 rounded-[14px] border border-canvas-line p-5 transition-colors duration-150 ease-out hover:border-ink/25 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_140px_24px] md:items-center md:gap-6 md:rounded-none md:border-0 md:border-b md:px-2 md:py-5 md:hover:bg-canvas-warm/60" data-id="element-258" data-mp="2d3mpsfk3lrss">
                      <span data-id="element-259" data-mp="g8qzj0hlb5ua">
                        <span className="block text-[17px] font-semibold text-ink transition-colors duration-150 ease-out group-hover:text-brand" data-id="element-260" data-mp="gbmxgotftuiq">
                          {job.title}
                        </span>
                        <span className="mt-0.5 block text-[14px] text-ink-muted" data-id="element-261" data-mp="2hvpvlz9okpn">{job.department}</span>
                      </span>
                      <span className="inline-flex items-center gap-1.5 text-[14px] text-ink-soft" data-id="element-262" data-mp="3tp869ysbsu4i">
                        <MapPinIcon className="h-4 w-4 shrink-0 text-ink-muted" aria-hidden="true" data-id="element-263" data-mp="3oo0yw0u3hwyd" />
                        {job.location} · {job.mode}
                      </span>
                      <span className="flex items-center justify-between text-[14px] text-ink-soft md:block" data-id="element-264" data-mp="1nmb80nzk510y">
                        {job.type}
                        <ArrowRightIcon className="h-5 w-5 text-brand md:hidden" aria-hidden="true" data-id="element-265" data-mp="1sl5y8azutrv3" />
                      </span>
                      <ArrowRightIcon className="hidden h-5 w-5 text-ink-muted transition-[transform,color] duration-150 ease-out group-hover:translate-x-0.5 group-hover:text-brand md:block" aria-hidden="true" data-id="element-266" data-mp="1iw4imlx81chn" />
                    </SmartLink>
                  </li>)}
              </ul>
            </section>)}
        </div>}
    </div>;
}
function Select({
  id,
  label,
  value,
  onChange,
  allLabel,
  options
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  allLabel: string;
  options: string[];
}) {
  return <div className="relative lg:w-[240px]" data-id="element-267" data-mp="2u9zw7q8h9d6r">
      <label htmlFor={id} className="sr-only" data-id="element-268" data-mp="inxtaw7z7t30">
        {label}
      </label>
      <select id={id} value={value} onChange={e => onChange(e.target.value)} className="h-12 w-full appearance-none rounded-[10px] border border-ink/15 bg-white pl-4 pr-10 text-[15px] font-medium text-ink outline-none transition-colors duration-150 ease-out hover:border-ink/30 focus:border-brand" data-id="element-269" data-mp="ilv4ugmh280e">
        <option value="" data-id="element-270" data-mp="11dw9jzlxgshq">{allLabel}</option>
        {options.map(o => <option key={o} value={o} data-id="element-271" data-mp="xcuapjvxaiga">
            {o}
          </option>)}
      </select>
      <ChevronDownIcon className="pointer-events-none absolute right-3.5 top-4 h-4 w-4 text-ink-muted" aria-hidden="true" data-id="element-272" data-mp="xf6rvutuwosa" />
    </div>;
}
function slugify(s: string) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-');
}